// @ts-nocheck
import React, { useState, useEffect } from "react";
import { 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  X, 
  Users, 
  Loader2, 
  ChevronLeft, 
  ChevronRight,
  ShieldAlert,
  CheckCircle
} from "lucide-react";
import { collection, query, onSnapshot, doc, deleteDoc, addDoc, updateDoc, serverTimestamp, orderBy } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";

const TestemunhasPage = () => {
  const { companyInfo } = useAuth();
  
  // Tipagem forte para o TypeScript/Linter não reclamar de array vazio
  const [testemunhas, setTestemunhas] = useState(/** @type {any[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; 

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(/** @type {string | null} */ (null));
  const [formData, setFormData] = useState({
    nome: "",
    cpf: "",
    email: "",
    telefone: "",
    role: "Testemunha" // Novo campo com valor padrão
  });

  // Efeito de trava de scroll
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  // Busca em tempo real
  useEffect(() => {
    if (!companyInfo?.id) return;

    const q = query(
      collection(db, "companies", companyInfo.id, "testemunhas"),
      orderBy("nome", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dados = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setTestemunhas(dados);
      setLoading(false);
    }, (error) => {
      console.error("Erro ao buscar testemunhas:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyInfo?.id]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  /** @param {any} testemunha */
  const handleOpenModal = (testemunha = null) => {
    if (testemunha) {
      setEditingId(testemunha.id);
      setFormData({
        nome: testemunha.nome || "",
        cpf: testemunha.cpf || "",
        email: testemunha.email || "",
        telefone: testemunha.telefone || "",
        role: testemunha.role || "Testemunha"
      });
    } else {
      setEditingId(null);
      setFormData({ nome: "", cpf: "", email: "", telefone: "", role: "Testemunha" });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({ nome: "", cpf: "", email: "", telefone: "", role: "Testemunha" });
  };

  /** @param {any} e */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /** @param {any} e */
  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.cpf || !formData.email) {
      alert("Por favor, preencha os campos obrigatórios (Nome, CPF e E-mail).");
      return;
    }

    setIsUpdating(true);
    try {
      if (editingId) {
        const docRef = doc(db, "companies", companyInfo.id, "testemunhas", editingId);
        await updateDoc(docRef, {
          ...formData,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, "companies", companyInfo.id, "testemunhas"), {
          ...formData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      handleCloseModal();
    } catch (error) {
      console.error("Erro ao salvar testemunha:", error);
      alert("Falha ao salvar a testemunha. Verifique sua conexão.");
    } finally {
      setIsUpdating(false);
    }
  };

  /** 
   * @param {string} id 
   * @param {string} nome 
   */
  const handleDelete = async (id, nome) => {
    const confirmar = window.confirm(`Tem certeza que deseja excluir a testemunha "${nome}"?`);
    if (!confirmar) return;

    setIsUpdating(true);
    try {
      await deleteDoc(doc(db, "companies", companyInfo.id, "testemunhas", id));
    } catch (error) {
      console.error("Erro ao excluir testemunha:", error);
      alert("Falha ao excluir o registro.");
    } finally {
      setIsUpdating(false);
    }
  };

  // Filtro e Paginação
  const testemunhasFiltradas = testemunhas.filter(t => 
    (t.nome && t.nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (t.cpf && t.cpf.includes(searchTerm)) ||
    (t.email && t.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (t.role && t.role.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = testemunhasFiltradas.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(testemunhasFiltradas.length / itemsPerPage);

  const nextPage = () => { if (currentPage < totalPages) setCurrentPage(prev => prev + 1); };
  const prevPage = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in bg-slate-50 min-h-full font-sans relative">
      
      {isUpdating && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-[2px] z-[50] flex items-center justify-center rounded-2xl">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
        </div>
      )}

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-brand-primary/10 flex items-center justify-center shrink-0 border border-brand-primary/20">
            <Users className="w-6 h-6 md:w-7 md:h-7 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">
              Banco de Testemunhas e Signatários
            </h1>
            <p className="text-sm sm:text-base text-slate-500 mt-0.5">
              Cadastre e gerencie as pessoas autorizadas para assinatura dos contratos.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row w-full md:w-auto items-center gap-4 mt-2 md:mt-0">
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar por nome, CPF ou papel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 md:py-2 border border-slate-200 rounded-xl md:rounded-lg leading-5 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-brand-primary sm:text-sm shadow-sm"
            />
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-brand-primary text-white px-6 py-3 md:py-2.5 rounded-xl font-bold text-sm transition-all shadow-md hover:brightness-110 shrink-0"
          >
            <Plus className="w-4 h-4" /> Novo Registro
          </button>
        </div>
      </div>

      {/* LISTAGEM */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        {testemunhasFiltradas.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 border border-slate-100">
              <ShieldAlert className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Nenhum registro encontrado</h3>
            <p className="text-slate-500 text-sm">
              {searchTerm ? "Nenhum resultado corresponde à sua busca." : "Adicione seu primeiro signatário para acelerar a confecção dos envelopes do ClickSign."}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-[11px] tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4">Nome Completo</th>
                    <th className="px-6 py-4">Papel no Contrato</th>
                    <th className="px-6 py-4">CPF</th>
                    <th className="px-6 py-4">Contato (E-mail / Telefone)</th>
                    <th className="px-6 py-4 text-center w-24">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentItems.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50 transition-colors group">
                      <td className="px-6 py-4 font-bold text-slate-800">
                        {t.nome}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
                          {t.role || "Testemunha"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600 font-medium">
                        {t.cpf}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-slate-700 font-medium">{t.email}</span>
                          <span className="text-xs text-slate-400 mt-0.5">{t.telefone || "Sem telefone"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-3">
                          <button 
                            onClick={() => handleOpenModal(t)}
                            className="text-slate-400 hover:text-brand-primary transition-colors p-1 bg-white border border-slate-200 rounded-lg hover:border-brand-primary/30 shadow-sm"
                            title="Editar Testemunha"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDelete(t.id, t.nome)}
                            className="text-slate-400 hover:text-red-500 transition-colors p-1 bg-white border border-slate-200 rounded-lg hover:border-red-200 hover:bg-red-50 shadow-sm"
                            title="Excluir Testemunha"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINAÇÃO */}
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-500 shrink-0">
              <span className="font-medium text-center sm:text-left">
                Mostrando <strong className="text-slate-800">{indexOfFirstItem + 1}</strong> a <strong className="text-slate-800">{Math.min(indexOfLastItem, testemunhasFiltradas.length)}</strong> de <strong className="text-slate-800">{testemunhasFiltradas.length}</strong>
              </span>
              <div className="flex items-center gap-4 w-full sm:w-auto justify-center">
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button onClick={prevPage} disabled={currentPage === 1} className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50 transition-colors shadow-sm">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-bold text-slate-800 px-3 py-1 bg-white border border-slate-200 rounded-lg shadow-sm">
                      {currentPage} / {totalPages}
                    </span>
                    <button onClick={nextPage} disabled={currentPage >= totalPages} className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-50 transition-colors shadow-sm">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* MODAL DE CRIAÇÃO/EDIÇÃO */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 animate-fade-in overflow-hidden">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleCloseModal}></div>
          <div className="relative bg-white w-full max-w-lg flex flex-col rounded-2xl shadow-2xl overflow-hidden border border-slate-200 max-h-full">
            
            <div className="p-5 sm:p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50 shrink-0">
              <h2 className="text-lg font-bold flex items-center gap-2 text-brand-primary">
                {editingId ? <Edit2 className="w-5 h-5"/> : <Plus className="w-5 h-5"/>}
                {editingId ? 'Editar Signatário' : 'Novo Signatário'}
              </h2>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-slate-600 p-2 bg-white border border-slate-200 rounded-full transition-colors shadow-sm">
                <X className="w-4 h-4"/>
              </button>
            </div>

            <form id="testemunhaForm" onSubmit={handleSave} className="p-5 sm:p-6 flex-1 min-h-0 overflow-y-auto space-y-5 bg-white custom-scrollbar">
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Papel no Contrato <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  required
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-brand-primary font-medium text-slate-800 shadow-sm"
                >
                  <option value="Testemunha">Testemunha</option>
                  <option value="Imobiliária (Procuração)">Imobiliária (Procuração)</option>
                  <option value="Diretor">Diretor</option>
                  <option value="Representante Legal">Representante Legal</option>
                  <option value="Outros">Outros</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Nome Completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nome"
                  required
                  value={formData.nome}
                  onChange={handleInputChange}
                  placeholder="Ex: João da Silva"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-brand-primary font-medium text-slate-800 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  CPF <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="cpf"
                  required
                  value={formData.cpf}
                  onChange={handleInputChange}
                  placeholder="000.000.000-00"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-brand-primary font-medium text-slate-800 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  E-mail <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="joao@exemplo.com"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-brand-primary font-medium text-slate-800 shadow-sm"
                />
                <p className="text-[10px] text-slate-400 mt-1.5 font-medium">Este e-mail será usado para o envio do link de assinatura via ClickSign.</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Telefone / WhatsApp
                </label>
                <input
                  type="text"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleInputChange}
                  placeholder="(00) 00000-0000"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary focus:border-brand-primary font-medium text-slate-800 shadow-sm"
                />
              </div>

            </form>

            <div className="p-5 sm:p-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 shrink-0">
              <button 
                type="button" 
                onClick={handleCloseModal} 
                className="px-6 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 rounded-full transition-colors shadow-sm"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                form="testemunhaForm"
                disabled={isUpdating}
                className="px-6 py-2.5 text-sm font-bold bg-brand-primary text-white rounded-full transition-colors shadow-md hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUpdating ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle className="w-4 h-4"/>}
                Salvar Registro
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default TestemunhasPage;